import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Home,
  Server,
  Rocket,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Book,
  HelpCircle,
  RefreshCw,
  ClipboardList,
  Folder,
  Sparkles,
  Phone,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { getBranches } from '@/api/deployments';

interface DashboardLayoutProps {
  children: React.ReactNode;
  headerAction?: React.ReactNode;
  /** If true, main content won't scroll (useful for pages that manage their own scrolling like Deployments) */
  noScroll?: boolean;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  /** Show if project has code_server_enabled (AI features) */
  codeServerOnly?: boolean;
}

export function DashboardLayout({ children, headerAction, noScroll = false }: DashboardLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const { user, projects, logout } = useAuth();

  const currentProject = projects.find(p => p.id === projectId) || projects[0];

  // Check if project has code_server_enabled (for AI features access)
  const { data: branchesData } = useQuery({
    queryKey: ['branches', projectId],
    queryFn: () => getBranches(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Check if any build has code_server_enabled
  const hasCodeServerEnabled = branchesData?.branches_with_builds?.some(
    branch => branch.latest_build?.code_server_enabled
  ) ?? false;

  // Navigation items for when viewing a project
  const projectNavigation: NavItem[] = projectId ? [
    { name: 'Projects', href: '/projects', icon: Home },
    { name: 'Servers', href: `/project/${projectId}/servers`, icon: Server },
    { name: 'Deployments', href: `/project/${projectId}/deployments`, icon: Rocket },
    { name: 'doploy AI', href: `/project/${projectId}/ai`, icon: Sparkles, codeServerOnly: true },
    { name: 'Insights', href: `/project/${projectId}/insights`, icon: Phone, codeServerOnly: true },
    { name: 'Backups', href: `/project/${projectId}/backups`, icon: RefreshCw },
    { name: 'Tasks', href: `/project/${projectId}/tasks`, icon: ClipboardList, adminOnly: true },
    { name: 'Audit Logs', href: `/project/${projectId}/audit-logs`, icon: ClipboardList },
    { name: 'Project Settings', href: `/project/${projectId}/settings`, icon: Settings },
  ] : [];

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="bg-gray-50 h-screen flex flex-col overflow-hidden">
      {/* Header Bar */}
      <header className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Left side - Logo and Project Selector */}
            <div className="flex items-center gap-4">
              {/* Logo */}
              <Link to="/projects" className="flex items-center">
                <img 
                  src="/images/doploy-logo.png" 
                  alt="doploy" 
                  className="h-6"
                />
              </Link>

              {/* Project Selector */}
              {projects.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="hidden md:flex items-center gap-2"
                    >
                      <Folder className="h-4 w-4" />
                      <span>{currentProject?.name || 'Projects'}</span>
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {projects.map((project) => (
                      <DropdownMenuItem key={project.id} asChild>
                        <Link to={`/project/${project.id}/deployments`} className="flex items-center gap-2">
                          <Folder className="h-4 w-4" />
                          {project.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/projects" className="flex items-center gap-2">
                        <Home className="h-4 w-4" />
                        View all projects
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Right side - Header Action, Docs, Help, User Menu */}
            <div className="flex items-center gap-2">
              {/* Page-specific Header Action */}
              {headerAction && (
                <div className="hidden md:block">
                  {headerAction}
                </div>
              )}

              {/* Docs Button */}
              <Button 
                variant="outline" 
                size="sm" 
                className="hidden md:flex"
                asChild
              >
                <a href="https://docs.doploy.io" target="_blank" rel="noopener noreferrer">
                  <Book className="h-4 w-4 mr-1" />
                  Docs
                </a>
              </Button>

              {/* Get Help Button */}
              <Button 
                variant="outline" 
                size="sm" 
                className="hidden md:flex"
                asChild
              >
                <a href="mailto:support@doploy.io">
                  <HelpCircle className="h-4 w-4 mr-1" />
                  Get help
                </a>
              </Button>

              {/* User Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    <div className="hidden lg:block text-left">
                      {user?.full_name ? (
                        <>
                          <span className="text-sm font-medium">{user.full_name}</span>
                          <span className="text-xs text-muted-foreground block">{user.email}</span>
                        </>
                      ) : (
                        <span className="text-sm">{user?.email}</span>
                      )}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      Account Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a href="mailto:support@doploy.io" className="flex items-center gap-2">
                      <HelpCircle className="h-4 w-4" />
                      Need help?
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Bar (only show when in a project context) */}
      {projectId && (
        <nav className="bg-white border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            {/* Desktop Navigation */}
            <div className="hidden lg:flex h-12 items-center gap-1 overflow-x-auto">
              {projectNavigation.map((item) => {
                const isActive = location.pathname === item.href ||
                  (item.href !== '/projects' && location.pathname.startsWith(item.href));

                // Skip admin-only items for non-admins
                if (item.adminOnly && !user?.is_admin) return null;
                // Skip code_server_enabled items if project doesn't have it enabled
                if (item.codeServerOnly && !hasCodeServerEnabled) return null;

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap",
                      isActive
                        ? "text-primary bg-primary/5"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* Mobile Navigation */}
            {mobileMenuOpen && (
              <div className="lg:hidden py-2 space-y-1">
                {projectNavigation.map((item) => {
                  const isActive = location.pathname === item.href ||
                    (item.href !== '/projects' && location.pathname.startsWith(item.href));

                  if (item.adminOnly && !user?.is_admin) return null;
                  if (item.codeServerOnly && !hasCodeServerEnabled) return null;

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors",
                        isActive
                          ? "text-primary bg-primary/5"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                      )}
                    >
                      <item.icon className="h-4 w-4" />
                      {item.name}
                    </Link>
                  );
                })}
                
                {/* Mobile-only links */}
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <a
                    href="https://docs.doploy.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                  >
                    <Book className="h-4 w-4" />
                    Docs
                  </a>
                  <a
                    href="mailto:support@doploy.io"
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                  >
                    <HelpCircle className="h-4 w-4" />
                    Get help
                  </a>
                </div>
              </div>
            )}
          </div>
        </nav>
      )}

      {/* Main Content */}
      <main className={cn("flex-1 py-6", noScroll ? "overflow-hidden" : "overflow-auto")}>
        <div className={cn("px-4 sm:px-6 lg:px-8", noScroll && "h-full")}>
          {children}
        </div>
      </main>
    </div>
  );
}
